import { IPStack } from 'baseComponents/Stack/IPStack';
import { eventBannerRegistrationUrl } from 'components/NavBar/IsoEventBanner';
import { TFunction } from 'i18next';

//
// Definition
//
export enum CoachTypes {
  Timeline = 1,
  Inheritance = 2,
  TaskFilterLocation = 3,
  FormsMoved = 4,
  TagsMoved = 5,
  // Value bumped from 6 to 7 when the registration link was added to the bubble, so users who had
  // already dismissed the link-less version see the coachmark once more. Dismissal is stored per
  // value as a CoachMark activity, so the old value 6 records simply no longer match.
  InGoodCompany26 = 7,
  // The regrouped main menu of September 2026. A later menu change that needs announcing gets a new
  // value, so people who dismissed this one see the next.
  MainMenuChanged = 8,
}

export class CoachMarkDefinition {
  title: string;

  jsx?: JSX.Element;

  ctaText?: string;

  ctaUrl?: string;

  constructor(title: string) {
    this.title = title;
  }
}

//
// Timeline
//
export const getCoachMarkTimeline = (t: TFunction<string[]>) => {
  const cmTimeline = new CoachMarkDefinition(t('coachmarks:Timeline.Title'));
  cmTimeline.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:Timeline.Intro')}</IPStack.Item>
      <IPStack.Item>
        <ul>
          <li>{t('coachmarks:Timeline.Ctrl1')}</li>
          <li>{t('coachmarks:Timeline.Ctrl2')}</li>
          <li>{t('coachmarks:Timeline.Ctrl3')}</li>
          <li>{t('coachmarks:Timeline.Ctrl4')}</li>
          <li>{t('coachmarks:Timeline.Ctrl5')}</li>
          <li>{t('coachmarks:Timeline.Ctrl6')}</li>
        </ul>
      </IPStack.Item>
    </IPStack>
  );

  return cmTimeline;
};

//
// TaskFilterLocation
//
export const getCoachMarkTaskFilterLocation = (t: TFunction<string[]>) => {
  const cm = new CoachMarkDefinition(t('coachmarks:TaskFilterLocation.Title'));
  cm.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:TaskFilterLocation.Intro')}</IPStack.Item>
    </IPStack>
  );

  return cm;
};

//
// Inheritance
//
export const getCoachMarkInheritance = (t: TFunction<string[]>) => {
  const cmInheritance = new CoachMarkDefinition(t('coachmarks:Inheritance.Title'));
  cmInheritance.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:Inheritance.Intro')}</IPStack.Item>
      <IPStack.Item>
        <ul>
          <li>{t('coachmarks:Inheritance.Ctrl1')}</li>
          <li>{t('coachmarks:Inheritance.Ctrl2')}</li>
          <li>{t('coachmarks:Inheritance.Ctrl3')}</li>
        </ul>
      </IPStack.Item>
    </IPStack>
  );

  return cmInheritance;
};

//
// FormsMoved — sign-post coachmark shown where the old Forms tab used to be (forms now live under
// Content configuration). The link to the new location is rendered by the host scene, next to it.
//
export const getCoachMarkFormsMoved = (t: TFunction<string[]>) => {
  const cm = new CoachMarkDefinition(t('coachmarks:FormsMoved.Title'));
  cm.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:FormsMoved.Intro')}</IPStack.Item>
    </IPStack>
  );

  return cm;
};

//
// TagsMoved — sign-post coachmark shown where the old Library Tags tab used to be (tags now live
// under Content configuration).
//
export const getCoachMarkTagsMoved = (t: TFunction<string[]>) => {
  const cm = new CoachMarkDefinition(t('coachmarks:TagsMoved.Title'));
  cm.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:TagsMoved.Intro')}</IPStack.Item>
    </IPStack>
  );

  return cm;
};

//
// InGoodCompany26
//
export const getCoachMarkInGoodCompany26 = (t: TFunction<string[]>) => {
  const cm = new CoachMarkDefinition(t('coachmarks:InGoodCompany26.Title'));
  cm.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:InGoodCompany26.Intro')}</IPStack.Item>
    </IPStack>
  );
  cm.ctaText = t('coachmarks:InGoodCompany26.Link');
  cm.ctaUrl = eventBannerRegistrationUrl;

  return cm;
};

//
// MainMenuChanged -- on the Frameworks entry of the main menu, which holds Overview, Requirements
// and Controls. NavRoot shows it in the expanded rail, to users who can see that entry.
//
export const getCoachMarkMainMenuChanged = (t: TFunction<string[]>) => {
  const cm = new CoachMarkDefinition(t('coachmarks:MainMenuChanged.Title'));
  cm.jsx = (
    <IPStack>
      <IPStack.Item>{t('coachmarks:MainMenuChanged.Intro')}</IPStack.Item>
    </IPStack>
  );
  cm.ctaText = t('coachmarks:MainMenuChanged.Link');

  return cm;
};
